import { Link } from 'react-router-dom'
import { DataTable, type DataTableColumn } from '@/components/DataTable'
import type { BundleSummary } from '@/types/bundle'

interface ActivityRow extends BundleSummary {
  fromVersion?: string
}

function formatDate(value?: string): string {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// Last 5 entries from `['bundles']` (already newest-first). "View diff"'s
// `from` is the next-older entry in the *full* fetched array (the backend
// returns every version unpaginated), not just the displayed 5 - so row 5
// can still resolve a valid `from` whenever a 6th+ bundle really exists.
// Disabled only when no older version exists at all (the true first release).
export function RecentActivityList({ bundles }: { bundles: BundleSummary[] }) {
  const rows: ActivityRow[] = bundles.slice(0, 5).map((bundle, index) => ({
    ...bundle,
    fromVersion: bundles[index + 1]?.version,
  }))

  const columns: DataTableColumn<ActivityRow>[] = [
    { key: 'version', header: 'Version', render: (row) => row.version },
    { key: 'publishedAt', header: 'Published', render: (row) => formatDate(row.publishedAt) },
    { key: 'releaseNote', header: 'Release note', render: (row) => row.releaseNote ?? '-' },
    {
      key: 'diff',
      header: '',
      align: 'right',
      render: (row) =>
        row.fromVersion ? (
          <Link
            to={`/diff-validation?from=${row.fromVersion}&to=${row.version}`}
            className="text-accent hover:underline"
          >
            View diff
          </Link>
        ) : (
          <span className="text-text-disabled">View diff</span>
        ),
    },
  ]

  return <DataTable columns={columns} data={rows} getRowKey={(row) => row.version} />
}
